/*
  Cash Box page for FullStack.cash
*/

import React from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { Container, Row, Col, Card, Button } from 'react-bootstrap'
import cashBoxImage from '../assets/images/cash-box01.png'

function CashBox () {
  return (
    <>
      <Helmet>
        <title>Cash Box | FullStack.cash</title>
      </Helmet>

      <Container className='py-5'>
        <Row className='mb-5'>
          <Col md={6}>
            <h2>
              Cash Box: Your Personal
              <br /> Blockchain Server
            </h2>
            <p>
              The Cash Box is a custom-built computer appliance, built around a
              Raspberry Pi 5. It features 16GB of RAM and a 2TB NVMe hard drive
              with a touch screen interface. The Cash Box runs all the software in
              the{' '}
              <a
                href='https://cashstack.info'
                target='_blank'
                rel='noreferrer'
              >
                Cash Stack
              </a>
              , which includes all the APIs offered by FullStack.cash.
            </p>
            <p>
              Running a Cash Box provides you with the same infrastructure as the
              full private infrastructure described on our{' '}
              <Link to='/pricing'>Pricing page</Link>. This includes the bch-api
              REST API, Bitcoin Cash (BCHN) Full Node,
              Fulcrum ElectrumX Indexer, and SLP Indexer - all running locally on
              your hardware.
            </p>
            <p>
              The Cash Box allows unrestricted access to the BCH blockchain without
              any rate limits. It's perfect for local development by hobbyists,
              small companies, or large enterprises. Essentially a personal server
              running the entire Cash Stack, including all services offered by
              FullStack.cash, for under $1,000 USD.
            </p>
            <div className='text-center'>
              <Button
                href='https://woodcashbox.com'
                target='_blank'
                rel='noreferrer'
                variant='primary'
                size='lg'
              >
                Learn More & Purchase
              </Button>
            </div>
          </Col>
          <Col md={6}>
            <img src={cashBoxImage} alt='Cash Box' className='img-fluid' />
          </Col>
        </Row>

        <Row>
          <Col>
            <h2 className='text-center mb-4'>Hardware Specifications</h2>
            <p className='text-center mb-4'>
              The Cash Box is built with quality components to ensure reliable
              operation of the entire Cash Stack infrastructure.
            </p>
          </Col>
        </Row>

        <Row className='g-4 mb-5'>
          <Col md={4}>
            <Card className='h-100'>
              <Card.Body>
                <Card.Title>
                  <strong>Raspberry Pi 5</strong>
                </Card.Title>
                <Card.Text>
                  Built around the powerful Raspberry Pi 5 single-board computer,
                  providing the computing power needed to run all Cash Stack
                  services efficiently.
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className='h-100'>
              <Card.Body>
                <Card.Title>
                  <strong>16GB RAM</strong>
                </Card.Title>
                <Card.Text>
                  Ample memory for running multiple blockchain services
                  simultaneously, ensuring smooth operation of all APIs and indexers.
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className='h-100'>
              <Card.Body>
                <Card.Title>
                  <strong>2TB NVMe Storage</strong>
                </Card.Title>
                <Card.Text>
                  Fast NVMe solid-state storage provides plenty of space for the
                  Bitcoin Cash and eCash blockchain databases, ensuring quick access
                  to blockchain data.
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className='h-100'>
              <Card.Body>
                <Card.Title>
                  <strong>Touch Screen Display</strong>
                </Card.Title>
                <Card.Text>
                  Integrated touch screen interface for easy monitoring and
                  interaction with your Cash Box, making it simple to check status
                  and manage services.
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col>
            <h2 className='text-center mb-4'>Included Infrastructure</h2>
            <p className='text-center mb-4'>
              Every Cash Box includes the complete suite of blockchain infrastructure
              services, identical to what we offer for private hosting. Run
              everything locally with no rate limits and complete control over your
              infrastructure.
            </p>
          </Col>
        </Row>

        <Row className='g-4 mb-5'>
          <Col md={6}>
            <Card className='h-100'>
              <Card.Body>
                <Card.Title>
                  <strong>bch-api REST API</strong>
                </Card.Title>
                <Card.Text>
                  Full REST API access for interacting with the Bitcoin Cash
                  blockchain. This is the same API infrastructure offered on our
                  hosted services, now running locally on your Cash Box.
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className='h-100'>
              <Card.Body>
                <Card.Title>
                  <strong>Bitcoin Cash (BCHN) Full Node</strong>
                </Card.Title>
                <Card.Text>
                  Complete Bitcoin Cash full node running locally, giving you
                  direct access to the BCH blockchain without relying on external
                  services.
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className='h-100'>
              <Card.Body>
                <Card.Title>
                  <strong>Fulcrum ElectrumX Indexer</strong>
                </Card.Title>
                <Card.Text>
                  High-performance indexing service for fast wallet queries and
                  transaction lookups, essential for building responsive blockchain
                  applications.
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className='h-100'>
              <Card.Body>
                <Card.Title>
                  <strong>SLP Indexer</strong>
                </Card.Title>
                <Card.Text>
                  Specialized indexer for Simple Ledger Protocol (SLP) tokens,
                  enabling fast queries for SLP token balances and transactions.
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col>
            <h2 className='text-center mb-4'>Perfect For</h2>
            <p className='text-center mb-4'>
              Whether you're an individual developer, a startup, or a large
              enterprise, the Cash Box provides the blockchain infrastructure you
              need at a fraction of the cost of hosted services.
            </p>
          </Col>
        </Row>

        <Row className='g-4 mb-5'>
          <Col md={4}>
            <Card className='h-100 text-center'>
              <Card.Body>
                <i className='fas fa-user fa-3x text-primary mb-3' />
                <Card.Title>
                  <strong>Hobbyists</strong>
                </Card.Title>
                <Card.Text>
                  Perfect for developers experimenting with Bitcoin Cash
                  development, learning blockchain technology, or building personal
                  projects with unrestricted API access.
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className='h-100 text-center'>
              <Card.Body>
                <i className='fas fa-building fa-3x text-primary mb-3' />
                <Card.Title>
                  <strong>Small Companies</strong>
                </Card.Title>
                <Card.Text>
                  Ideal for startups and small businesses that need reliable
                  blockchain infrastructure without the ongoing monthly costs of
                  hosted services. One-time purchase, unlimited usage.
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className='h-100 text-center'>
              <Card.Body>
                <i className='fas fa-industry fa-3x text-primary mb-3' />
                <Card.Title>
                  <strong>Large Enterprises</strong>
                </Card.Title>
                <Card.Text>
                  Great for companies that need local development environments,
                  testing infrastructure, or want to reduce dependency on external
                  API services. Deploy multiple Cash Boxes for different teams or
                  projects.
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className='mb-5'>
          <Col className='text-center'>
            <h2 className='mb-4'>Get Your Cash Box Today</h2>
            <p className='mb-4'>
              Visit{' '}
              <a
                href='https://woodcashbox.com'
                target='_blank'
                rel='noreferrer'
              >
                WoodCashBox.com
              </a>{' '}
              to learn more about the Cash Box, view detailed specifications, and
              place your order. Get complete blockchain infrastructure for under
              $1,000 USD.
            </p>
            <Button
              href='https://woodcashbox.com'
              target='_blank'
              rel='noreferrer'
              variant='primary'
              size='lg'
            >
              Visit WoodCashBox.com
            </Button>
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default CashBox
